import type { SubmittedItem } from './types';
import { Flex } from '@repo/ui/Flex';
import s from './ItemDisplay.module.css';
import { ItemCard } from '../ItemCard/ItemCard';
import { ShipmentGroup } from '../ShipmentGroup/ShipmentGroup';
import { useShipmentMetadata } from '../../hooks/useShipmentGroups';

export const ItemDisplay = ({
  items,
  showShipmentDetails = false,
}: {
  items: SubmittedItem[];
  hideImage?: boolean;
  showShipmentDetails?: boolean;
}) => {
  const { shipmentGroups, hasMultipleShipments } = useShipmentMetadata();

  // If no multiple shipments, render items normally
  if (!hasMultipleShipments || !showShipmentDetails) {
    return (
      <Flex direction="column" gap="g-16" className={s.items}>
        {items.map((item) => {
          const { quantity: _quantity, ...itemWithoutQuantity } = item;
          const itemProp = {
            ...itemWithoutQuantity,
            name: item.title || item.name,
          };

          const additionalDetails = [
            {
              name: 'Quantity',
              value: `${item.quantity}`,
            },
          ];

          return (
            <ItemCard
              key={item.id}
              item={itemProp}
              additionalDetails={additionalDetails}
              imageSize="sm"
            />
          );
        })}
      </Flex>
    );
  }

  // Group items by shipment for display
  return (
    <Flex direction="column" gap="g-24">
      {shipmentGroups.map((shipmentGroup) => {
        // Filter submitted items for this shipment
        const itemsInShipment = items.filter((item) =>
          shipmentGroup.items.some(
            (shipmentItem) => shipmentItem.id === item.id,
          ),
        );

        if (itemsInShipment.length === 0) return null;

        return (
          <ShipmentGroup
            key={shipmentGroup.shipmentIndex}
            shipmentGroup={{
              ...shipmentGroup,
              items: itemsInShipment.map((item) => {
                const { quantity: _quantity, ...itemWithoutQuantity } = item;
                return {
                  ...itemWithoutQuantity,
                  name: item.title || item.name,
                  quantity: {
                    ordered: item.quantity,
                    pending: 0,
                    completed: 0,
                    remaining: item.quantity,
                  },
                };
              }),
            }}
            imageSize="sm"
            showShipmentDetails={showShipmentDetails}
            showMoreCutoff={3}
          />
        );
      })}
    </Flex>
  );
};
